import React, { useState } from 'react';
import {
  Card,
  Upload,
  Button,
  message,
  Steps,
  Table,
  Space,
  Typography,
  Alert,
  Progress,
  Tag,
  Tooltip
} from 'antd';
import {
  InboxOutlined,
  DownloadOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import type { UploadProps, TableColumnsType } from 'antd';
import {
  downloadImportTemplate,
  parseImportFile,
  evaluateData,
  EVALUATION_CONFIG,
  type ImportRowData,
  type EvaluationResult
} from '../utils/importTemplate';
import { getTrainingStandards as fetchTrainingStandards, uploadProcessedData } from '../services/api';
import { CITY_LEVELS } from '../utils/constants';

const { Step } = Steps;
const { Title, Text } = Typography;
const { Dragger } = Upload;

interface ProcessedData extends ImportRowData {
  id: string;
  evaluation: EvaluationResult | null;
  hasError?: boolean;
  errorMessage?: string;
}

const ImportData: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedData, setUploadedData] = useState<ProcessedData[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  // 从API获取培训标准数据
  const getTrainingStandards = async () => {
    try {
      // 使用统一的API服务，获取所有培训标准数据
      const result = await fetchTrainingStandards({ limit: 1000 });
      return result.data || [];
    } catch (error) {
      console.error('获取培训标准数据失败:', error);
      throw error;
    }
  };

  const downloadTemplate = () => {
    try {
      downloadImportTemplate();
      message.success('模板下载成功');
    } catch (error) {
      message.error('模板下载失败');
    }
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx,.xls',
    beforeUpload: (file) => {
      handleFileUpload(file);
      return false;
    },
    showUploadList: false,
  };

  const handleFileUpload = async (file: File) => {
    setIsProcessing(true);
    setProcessingProgress(0);
    setOriginalFile(file); // 保存原始文件
    
    try {
      // 解析文件
      setProcessingProgress(30);
      const data = await parseImportFile(file);
      
      // 处理数据和评定
      setProcessingProgress(60);
      
      // 获取培训标准数据
      const standards = await getTrainingStandards();
      
      const processedData: ProcessedData[] = data.map((row, index) => {
        const id = `${Date.now()}-${index}`;
        
        // 查找对应的培训标准
        // 先按精确匹配（业务名称+要素名称+城市等级）
        let standard = standards.find((s: any) => 
          s.business_name === row.businessName && 
          s.element_name === row.elementName &&
          (s.city_level === row.cityLevel || s.city_level === null)
        );
        
        // 如果没找到，尝试匹配通用标准（不限城市等级）
        if (!standard) {
          standard = standards.find((s: any) => 
            s.business_name === row.businessName && 
            s.element_name === row.elementName &&
            s.city_level === null
          );
        }
        
        let evaluation: EvaluationResult | null = null;
        let hasError = false;
        let errorMessage = '';
        
        if (!standard) {
          hasError = true;
          errorMessage = `未找到匹配的培训标准: ${row.businessName} - ${row.elementName}`;
        } else {
          try {
            // 确保standard有必需的字段
            if (!standard.business_name || !standard.element_name) {
              hasError = true;
              errorMessage = '培训标准数据不完整';
            } else {
              // 转换为evaluateData需要的格式
              const standardData = {
                id: standard.id,
                business_id: standard.business_id,
                element_id: standard.element_id,
                business_name: standard.business_name,
                element_name: standard.element_name,
                city_level: standard.city_level,
                min_range: standard.min_range,
                max_range: standard.max_range,
                unit: standard.unit,
                is_active: standard.is_active
              };
              evaluation = evaluateData(row.actualValue, standardData, row.cityLevel);
            }
          } catch (error) {
            hasError = true;
            errorMessage = `评定计算失败: ${(error as Error).message}`;
          }
        }
        
        return {
          ...row,
          id,
          evaluation,
          hasError,
          errorMessage
        };
      });
      
      setProcessingProgress(100);
      setUploadedData(processedData);
      setCurrentStep(1);
      message.success(`成功解析 ${data.length} 条数据`);
      
    } catch (error) {
      message.error('文件解析失败: ' + (error as Error).message);
    } finally {
      setIsProcessing(false);
      setTimeout(() => setProcessingProgress(0), 1000);
    }
  };

  const confirmImport = async () => {
    if (!originalFile) {
      message.error('原始文件丢失，请重新上传');
      return;
    }

    console.log('🔍 开始导入确认:', {
      fileName: originalFile.name,
      fileSize: originalFile.size,
      fileType: originalFile.type,
      dataCount: uploadedData.length
    });

    // 检查是否有错误数据
    const errorCount = uploadedData.filter(item => item.hasError).length;
    if (errorCount > 0) {
      console.warn('❌ 发现错误数据:', errorCount, '条');
      message.error('存在错误数据，请修复后再导入');
      return;
    }

    try {
      message.info('正在保存数据到系统...');
      setIsProcessing(true);
      
      console.log('📤 开始调用后端API...');
      console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);
      
      // 使用新的API直接上传已处理的数据（包含前端评定结果）
      const processedDataForUpload = uploadedData.map(item => ({
        companyName: item.companyName,
        businessName: item.businessName,
        elementName: item.elementName,
        actualValue: item.actualValue,
        cityLevel: item.cityLevel,
        responsiblePerson: item.responsiblePerson,
        checkDate: item.checkDate,
        unit: '元',
        evaluation: item.evaluation
      }));
      
      const result = await uploadProcessedData(processedDataForUpload);
      
      console.log('✅ 导入API响应:', result);
      
      if (result) {
        message.success(`数据导入成功！共处理 ${uploadedData.length} 条记录`);
        setCurrentStep(2);
        
        // 重置状态
        setTimeout(() => {
          setCurrentStep(0);
          setUploadedData([]);
          setOriginalFile(null);
        }, 3000);
      } else {
        throw new Error('导入结果异常');
      }
      
    } catch (error) {
      console.error('❌ 导入失败详情:', {
        error: error,
        message: (error as Error).message,
        stack: (error as Error).stack
      });
      message.error('数据导入失败: ' + (error as Error).message);
    } finally {
      setIsProcessing(false);
    }
  };

  const previewColumns: TableColumnsType<ProcessedData> = [
    {
      title: '业务所属创大',
      dataIndex: 'companyName',
      width: 120,
    },
    {
      title: '培训业务',
      dataIndex: 'businessName',
      width: 100,
    },
    {
      title: '培训要素',
      dataIndex: 'elementName',
      width: 100,
    },
    {
      title: '检查结果',
      dataIndex: 'actualValue',
      width: 100,
      render: (value) => `${value}`,
    },
    {
      title: '城市等级',
      dataIndex: 'cityLevel',
      width: 100,
      render: (level) => CITY_LEVELS[level as keyof typeof CITY_LEVELS],
    },
    {
      title: '参考范围',
      dataIndex: 'evaluation',
      width: 120,
      render: (evaluation: EvaluationResult | null, record) => {
        if (record.hasError) {
          return (
            <Tooltip title={record.errorMessage}>
              <span style={{ color: '#ff4d4f', cursor: 'help' }}>未找到标准</span>
            </Tooltip>
          );
        }
        return evaluation ? evaluation.referenceRange : '-';
      },
    },
    {
      title: '评定结果',
      dataIndex: 'evaluation',
      width: 120,
      render: (evaluation: EvaluationResult | null, record) => {
        if (record.hasError) {
          return (
            <Tooltip title={record.errorMessage}>
              <Tag color="red" style={{ cursor: 'help' }}>错误</Tag>
            </Tooltip>
          );
        }
        if (!evaluation) return '-';
        
        // 详细调试信息
        console.log('🔍 详细渲染调试:', {
          evaluation: evaluation,
          status: evaluation.status,
          statusType: typeof evaluation.status,
          allConfigKeys: Object.keys(EVALUATION_CONFIG),
          hasConfig: !!EVALUATION_CONFIG[evaluation.status],
          configValue: EVALUATION_CONFIG[evaluation.status]
        });
        
        const config = EVALUATION_CONFIG[evaluation.status];
        
        // 安全检查，确保config存在
        if (!config) {
          console.warn('❌ 找不到配置:', {
            status: evaluation.status,
            statusType: typeof evaluation.status,
            availableStatuses: Object.keys(EVALUATION_CONFIG)
          });
          return <span style={{ color: '#666', fontWeight: 'bold' }}>未知状态({evaluation.status})</span>;
        }
        
        return (
          <Tooltip 
            title={
              <div>
                <div><strong>{config.text}</strong></div>
                <div>{config.description}</div>
                {evaluation.evaluation && (
                  <div style={{ marginTop: '4px' }}>
                    详情: {evaluation.evaluation}
                  </div>
                )}
              </div>
            }
            placement="topLeft"
          >
            <span 
              style={{ 
                color: config.color, 
                cursor: 'help',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              {config.icon} {config.text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'hasError',
      width: 100,
      render: (hasError, record) => {
        if (hasError) {
          return (
            <Tooltip title={record.errorMessage} placement="topLeft">
              <Tag 
                color="red" 
                icon={<ExclamationCircleOutlined />}
                style={{ cursor: 'help' }}
              >
                错误
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            正常
          </Tag>
        );
      },
    },
  ];

  const errorCount = uploadedData.filter(item => item.hasError).length;
  const successCount = uploadedData.length - errorCount;

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}>数据导入</Title>
        
        <Steps current={currentStep} style={{ marginBottom: '24px' }}>
          <Step title="上传文件" description="选择Excel文件并解析" />
          <Step title="预览确认" description="检查数据和评定结果" />
          <Step title="导入完成" description="数据已成功导入系统" />
        </Steps>

        {currentStep === 0 && (
          <div>
            <Alert
              message="导入说明"
              description={
                <div>
                  <p>1. 请先下载导入模板，按照模板格式填写数据</p>
                  <p>2. 支持 .xlsx 和 .xls 格式的Excel文件</p>
                  <p>3. 系统将自动进行数据评定和标准对比</p>
                </div>
              }
              type="info"
              style={{ marginBottom: '24px' }}
            />

            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadTemplate}
                size="large"
              >
                下载导入模板
              </Button>

              <Dragger {...uploadProps} style={{ padding: '40px' }}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                <p className="ant-upload-hint">
                  支持 .xlsx 和 .xls 格式的Excel文件
                </p>
              </Dragger>

              {isProcessing && (
                <div>
                  <Text>正在处理文件...</Text>
                  <Progress percent={processingProgress} />
                </div>
              )}
            </Space>
          </div>
        )}

        {currentStep === 1 && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Text strong>数据统计：</Text>
                <Tag color="green">正常 {successCount}</Tag>
                <Tag color="red">错误 {errorCount}</Tag>
                <Text>共 {uploadedData.length} 条</Text>
              </Space>
            </div>

            {errorCount > 0 && (
              <Alert
                message="数据检查警告"
                description={
                  <div>
                    <p>发现 {errorCount} 条错误数据，请检查后重新导入</p>
                    <p style={{ color: '#666', fontSize: '12px' }}>
                      💡 提示：鼠标悬停在错误状态上可查看具体错误原因
                    </p>
                  </div>
                }
                type="warning"
                style={{ marginBottom: '16px' }}
              />
            )}

            <Table
              columns={previewColumns}
              dataSource={uploadedData}
              rowKey="id"
              size="small"
              scroll={{ x: 800 }}
              pagination={{ pageSize: 10 }}
              rowClassName={(record) => record.hasError ? 'error-row' : ''}
            />

            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <Space>
                <Button onClick={() => setCurrentStep(0)}>
                  重新上传
                </Button>
                <Button 
                  type="primary" 
                  disabled={errorCount > 0}
                  loading={isProcessing}
                  onClick={confirmImport}
                  icon={<UploadOutlined />}
                >
                  {isProcessing ? '正在导入...' : '确认导入'}
                </Button>
              </Space>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <CheckCircleOutlined 
              style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} 
            />
            <Title level={3}>导入完成</Title>
            <Text>数据已成功导入系统，您可以在检查报告页面查看详细信息</Text>
          </div>
        )}
      </Card>

      <style>{`
        .error-row {
          background-color: #fff1f0;
        }
      `}</style>
    </div>
  );
};

export default ImportData;